<template>
  <!--
    SourceItemsDialog — общий диалог источников проекции для lite-таблицы.
    Раньше каждый SourceItems-cell держал свои q-dialog/q-spinner и вложенную
    таблицу. Тут диалог один на всю таблицу: ячейка лишь эмитит запрос с ids/type,
    шелл вызывает open(ids, type). Логика загрузки 1:1 с легаси SourceItemsCellElement.
  -->
  <q-dialog no-shake :full-width="false" v-model="sourceItemsDisplay">
    <q-card class="inhdialog-container-narrow">
      <div class="inhdialog-header">
        <h1 class="dialog_name">{{ $t('source_projection_elements') }}</h1>
        <q-btn
          flat
          round
          icon="close"
          size="md"
          color="accent"
          :title="$t('close_label')"
          @click="sourceItemsDisplay = false"
        />
      </div>

      <div class="dialog_content">
        <div v-if="source_jobs.length" class="inner_container q-mx-sm">
          <job-card v-for="(j, j_i) in source_jobs" :job="j" :key="'srcjob_' + j_i" />
        </div>
        <div v-if="source_contacts.length" class="inner_container q-mx-sm">
          <person-card v-for="(con, con_i) in source_contacts" :person="con" :key="'srccon_' + con_i" />
        </div>
      </div>
    </q-card>
  </q-dialog>

  <q-dialog :full-width="true" v-model="sourceTableDisplay">
    <q-card class="inhdialog-container">
      <div class="inhdialog-header">
        <h1 class="dialog_name">{{ $t('source_projection_elements') }}</h1>
        <q-btn
          flat
          round
          icon="close"
          size="md"
          color="accent"
          :title="$t('close_label')"
          @click="sourceTableDisplay = false"
        />
      </div>

      <div class="dialog_content">
        <div
          v-if="aux.isTableLoading"
          style="text-align: center; margin-top: 20px; margin-bottom: 20px; width: 100%"
        >
          <q-spinner-dots color="primary" size="2em" />
        </div>

        <query-table
          class="full-width q-px-sm"
          v-show="sourceTableModel && sourceTableModel.HeaderRow && projectionParentEl"
          :hide-panel="true"
          :src-element="projectionParentEl"
          :is-sector="true"
          :sector-entity-ids="entity_ids"
          :paginationModel="pagination"
          @update:paginationModel="onUpdatePagination"
          :selectedEverything="selectedAll"
          @update:selectedEverything="onSelectedAllUpdate"
          :selectedEntities="selectedEntities"
          @update:selectedEntities="onSelectionUpdate"
          :unselectedEntities="unselectedEntities"
          @update:unselectedEntities="onUnSelectionUpdate"
          @movePage="() => {}"
          @refreshPage="getSourceItems(entity_ids, entity_type)"
          v-model="sourceTableModel"
        />
      </div>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";
import { CellDataIdListEntityType } from "@/models/etc/types";
import type {
  AnalyticsResultTableExtended,
  JobLight,
  PersonInList,
} from "@/models/interfaces/entity-settings/querytable/querytable";
import type { PaginationProps } from "@/models/base/common.selector";
import { auxStore } from "@/store/auxillary";
import { settingsStore } from "@/store/settings";
import { BaseItemTypes } from "@/components/dnd/ItemTypes";
import JobCard from "../../item-cards/JobCard.vue";
import PersonCard from "../../item-cards/PersonCard.vue";

const aux = auxStore();
const setstore = settingsStore();

const sourceItemsDisplay = ref(false);
const sourceTableDisplay = ref(false);
const source_jobs = ref<JobLight[]>([]);
const source_contacts = ref<PersonInList[]>([]);
const entity_ids = ref<number[]>([]);
const entity_type = ref<CellDataIdListEntityType>(0);
const sourceTableModel = ref<AnalyticsResultTableExtended>({} as AnalyticsResultTableExtended);
const projectionParentEl = ref<any>(undefined);
const pagination = reactive({} as PaginationProps);
const selectedEntities = ref<number[]>([]);
const unselectedEntities = ref<number[]>([]);
const selectedAll = ref<string>("");

function resetPagination() {
  Object.assign(pagination, {
    count: 0,
    offset: 0,
    recalculate: false,
    sortMode: 0,
    OrderByPinned: false,
    sortCategory: 0,
    report: false,
    total_count: 0,
    report_index: -1,
  });
}

async function getSourceItems(ids: number[], type: CellDataIdListEntityType) {
  entity_type.value = type;
  projectionParentEl.value = undefined;

  if (
    type === CellDataIdListEntityType.StorageItems ||
    type === CellDataIdListEntityType.StorageItemMovements
  ) {
    const chain = setstore.getProjectionParentChain;
    if (chain && chain.length) {
      projectionParentEl.value = {
        Id: chain[0].Identity.ElementId,
        elType:
          chain[0].Source !== undefined && chain[0].Source !== null
            ? BaseItemTypes.PROJECTION
            : chain[0].Identity.Kind === 0
            ? BaseItemTypes.SELECTION
            : BaseItemTypes.LIST,
        SrcType: chain[0].Identity.Source,
        DestType: chain[0].Identity.Destination,
      };
    }
  }

  switch (type) {
    case CellDataIdListEntityType.Jobs:
      source_jobs.value = [];
      sourceItemsDisplay.value = true;
      source_jobs.value = (await aux.getSourceJobs(ids)).data;
      break;
    case CellDataIdListEntityType.Persons:
      source_contacts.value = [];
      sourceItemsDisplay.value = true;
      source_contacts.value = (await aux.getSourcePersons(ids)).data;
      break;
    case CellDataIdListEntityType.StorageItems:
    case CellDataIdListEntityType.StorageItemMovements:
      resetPagination();
      entity_ids.value = [];
      sourceTableModel.value = {} as AnalyticsResultTableExtended;
      sourceTableDisplay.value = true;
      sourceTableModel.value = await aux.getSourceTable(ids);
      break;
    default:
      break;
  }
}

function onUpdatePagination() {}
function onSelectionUpdate(val: any) {
  selectedEntities.value = val;
}
function onUnSelectionUpdate(val: any) {
  unselectedEntities.value = val;
}
function onSelectedAllUpdate(val: any) {
  selectedAll.value = val;
}

// Вызывается из шелла по событию ячейки.
function open(ids: number[], type: number) {
  getSourceItems(ids, type as CellDataIdListEntityType);
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.inhdialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dialog_content {
  display: flex;
  flex-wrap: wrap;
}
</style>
